<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { Link } from 'svelte-routing';
  import { ArrowRight } from 'lucide-svelte';
  import PageHeader from '../lib/components/layout/PageHeader.svelte';
  import { Spinner } from '../lib/components/ui/index';
  import KpiTile from '../lib/components/ui/KpiTile.svelte';
  import { api } from '../lib/api';
  import { activeHouseholdStore } from '../lib/stores/activeHouseholdStore';
  import type { UsageStatsResponse, TimeSeriesPoint, DashboardStats } from '../lib/api';

  type UsageTimeRange = 'today' | '7d' | '1m' | 'all';
  const timeRangeOptions: { value: UsageTimeRange; labelKey: string }[] = [
    { value: 'today', labelKey: 'pages.settings.usage_range_today' },
    { value: '7d', labelKey: 'pages.settings.usage_range_7d' },
    { value: '1m', labelKey: 'pages.settings.usage_range_1m' },
    { value: 'all', labelKey: 'pages.settings.usage_range_all' },
  ];
  let usageTimeRange: UsageTimeRange = 'all';
  let usageStats: UsageStatsResponse | null = null;
  let globalStats: UsageStatsResponse | null = null;
  let usageLoading = false;
  let usageError: string | null = null;

  async function loadUsageStats() {
    usageLoading = true;
    usageError = null;
    try {
      const [filtered, global] = await Promise.all([
        api.settings.getUsageStats(usageTimeRange),
        globalStats ? Promise.resolve(globalStats) : api.settings.getUsageStats('all'),
      ]);
      usageStats = filtered;
      globalStats = global;
    } catch (err) {
      usageError = err instanceof Error ? err.message : $_('errors.generic');
    } finally {
      usageLoading = false;
    }
  }

  // "Dein Bestand": household-scoped, independent of the time range.
  let inventory: DashboardStats | null = null;
  let inventoryLoading = true;
  let inventoryError: string | null = null;

  async function loadInventory() {
    inventoryLoading = true;
    inventoryError = null;
    const requestedFor = $activeHouseholdStore;
    try {
      const result = await api.documents.getDashboardStats(requestedFor);
      // A faster household switch may have started a newer request — drop stale answers.
      if (requestedFor === $activeHouseholdStore) inventory = result;
    } catch (err) {
      if (requestedFor === $activeHouseholdStore)
        inventoryError = err instanceof Error ? err.message : $_('errors.generic');
    } finally {
      if (requestedFor === $activeHouseholdStore) inventoryLoading = false;
    }
  }

  // Reload on household switch; onMount handles the first load (no double-loading).
  let prevHouseholdId = $activeHouseholdStore;
  $: if ($activeHouseholdStore !== prevHouseholdId) {
    prevHouseholdId = $activeHouseholdStore;
    loadInventory();
  }

  function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  function formatDuration(seconds: number): string {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  }

  function formatNumber(n: number): string {
    return n.toLocaleString();
  }

  function computeTrendDirection(trend: TimeSeriesPoint[]): 'up' | 'down' | 'neutral' {
    if (trend.length < 2) return 'neutral';
    const mid = Math.floor(trend.length / 2);
    const firstHalf = trend.slice(0, mid).reduce((s, p) => s + p.value, 0) / mid;
    const secondHalf = trend.slice(mid).reduce((s, p) => s + p.value, 0) / (trend.length - mid);
    if (secondHalf > firstHalf * 1.05) return 'up';
    if (secondHalf < firstHalf * 0.95) return 'down';
    return 'neutral';
  }

  function resolveTrend(
    primary: TimeSeriesPoint[] | undefined | null,
    fallback: TimeSeriesPoint[] | undefined | null,
  ): TimeSeriesPoint[] {
    if (primary && primary.length >= 1) return primary;
    if (fallback && fallback.length >= 1) return fallback;
    return [];
  }

  function handleTimeRangeChange(range: UsageTimeRange) {
    usageTimeRange = range;
    loadUsageStats();
  }

  onMount(() => {
    loadInventory();
    loadUsageStats();
  });
</script>

<PageHeader
  title={$_('pages.settings.tab_usage')}
  breadcrumbs={[
    { label: $_('pages.settings.title'), href: '/settings' },
    { label: $_('pages.settings.tab_usage') },
  ]}
/>

<div class="max-w-5xl">
  <!-- Inventory Section (household-scoped, independent of time range) -->
  <h3 class="text-xs font-semibold uppercase tracking-wider text-text-muted mb-3">
    {$_('pages.settings.usage_section_inventory')}
  </h3>
  {#if inventoryLoading}
    <div class="flex justify-center py-6 mb-6">
      <Spinner />
    </div>
  {:else if inventoryError}
    <div class="px-3 py-2 mb-6 bg-error/10 border border-error/30 rounded text-sm text-error">
      {inventoryError}
    </div>
  {:else if inventory}
    <div class="mb-6">
      <div class="usage-grid">
        <KpiTile
          title={$_('pages.home.total_documents')}
          value={formatNumber(inventory.totalDocumentsHousehold)}
        />
        <KpiTile
          title={$_('pages.home.processing')}
          value={formatNumber(inventory.processingDocuments)}
        />
        <Link
          to="/inbox"
          class="group relative block rounded-lg transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <KpiTile
            title={$_('pages.home.unreviewed')}
            value={formatNumber(inventory.unreviewedDocuments)}
          />
          <ArrowRight
            class="absolute right-4 bottom-4 w-4 h-4 text-text-muted group-hover:text-text"
            strokeWidth={1.5}
          />
        </Link>
      </div>
      {#if inventory.totalDocumentsGlobal > inventory.totalDocumentsHousehold}
        <p class="text-xs text-text-muted mt-2">
          {$_('pages.home.total_global_hint')}: {formatNumber(inventory.totalDocumentsGlobal)}
        </p>
      {/if}
    </div>
  {/if}

  <!-- Time Range Selector -->
  <div class="flex gap-1 mb-6">
    {#each timeRangeOptions as range (range.value)}
      <button
        on:click={() => handleTimeRangeChange(range.value)}
        class="px-3 py-1.5 text-xs font-medium rounded transition-colors
          {usageTimeRange === range.value
          ? 'bg-accent text-on-accent'
          : 'bg-surface text-text-secondary border border-border hover:text-text'}"
      >
        {$_(range.labelKey)}
      </button>
    {/each}
  </div>

  {#if usageLoading}
    <div class="flex justify-center py-12">
      <Spinner />
    </div>
  {:else if usageError}
    <div class="px-3 py-2 bg-error/10 border border-error/30 rounded text-sm text-error">
      {usageError}
    </div>
  {:else if usageStats && globalStats}
    <!-- Documents Section (fixed, independent of time range) -->
    <h3 class="text-xs font-semibold uppercase tracking-wider text-text-muted mb-3">
      {$_('pages.settings.usage_section_documents')}
    </h3>
    <div class="usage-grid mb-6">
      <KpiTile
        title={$_('pages.settings.usage_total_documents')}
        value={formatNumber(globalStats.documents.totalCount)}
        trend={globalStats.documents.countTrend}
        trendDirection={computeTrendDirection(globalStats.documents.countTrend)}
      />
      <KpiTile
        title={$_('pages.settings.usage_per_household')}
        value={formatNumber(globalStats.documents.perHousehold.length)}
        unit={$_('pages.settings.usage_households_unit')}
      />
      <KpiTile
        title={$_('pages.settings.usage_per_person')}
        value={formatNumber(globalStats.documents.perPerson.length)}
        unit={$_('pages.settings.usage_persons_unit')}
      />
      <KpiTile
        title={$_('pages.settings.usage_avg_file_size')}
        value={formatBytes(usageStats.documents.avgFileSizeBytes)}
        trend={usageStats.documents.avgFileSizeTrend}
        trendDirection={computeTrendDirection(usageStats.documents.avgFileSizeTrend)}
      />
    </div>

    <!-- AI Usage Section -->
    <h3 class="text-xs font-semibold uppercase tracking-wider text-text-muted mb-3">
      {$_('pages.settings.usage_section_ai')}
    </h3>
    {@const aiCallsTrend = resolveTrend(
      usageStats.aiUsage.callsTrend,
      usageStats.aiUsage.tokensTrend,
    )}
    <div class="usage-grid mb-6">
      <KpiTile
        title={$_('pages.settings.usage_ai_total_calls')}
        value={formatNumber(usageStats.aiUsage.totalCalls)}
        trend={aiCallsTrend}
        trendDirection={computeTrendDirection(aiCallsTrend)}
      />
      <KpiTile
        title={$_('pages.settings.usage_ai_calls_per_doc')}
        value={usageStats.aiUsage.avgCallsPerDocument.toFixed(1)}
      />
    </div>

    <!-- Performance Section -->
    <h3 class="text-xs font-semibold uppercase tracking-wider text-text-muted mb-3">
      {$_('pages.settings.usage_section_performance')}
    </h3>
    <div class="usage-grid">
      <KpiTile
        title={$_('pages.settings.usage_avg_processing_duration')}
        value={formatDuration(usageStats.performance.avgProcessingDurationSeconds)}
        trend={usageStats.performance.durationTrend}
        trendDirection={computeTrendDirection(usageStats.performance.durationTrend)}
      />
    </div>
  {/if}
</div>

<style>
  .usage-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }

  @media (max-width: 768px) {
    .usage-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
